import React, { createContext, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { Filters, Searches, Sorts } from "../models/shipeezi.table";
import { Warehouse } from "../models/warehouse";
import { WarehouseMap } from "../models/warehouse.map";
import WarehousesMapsService from "../services/warehouse.map.service";
import { useAppState } from "./StateContext";

interface WarehouseContextProps {
  warehouse?: Warehouse;
  setWarehouse: React.Dispatch<React.SetStateAction<Warehouse | undefined>>;
  sectionId?: number;
  setSectionId: React.Dispatch<React.SetStateAction<number | undefined>>;
  searches?: Searches;
  setSearches: React.Dispatch<React.SetStateAction<Searches | undefined>>;
  sorting?: Sorts;
  setSorting: React.Dispatch<React.SetStateAction<Sorts | undefined>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  getAllWarehouseMaps: () => void;
  isFetchingWarehouseMaps: boolean;
  selectedMap?: WarehouseMap;
  setSelectedMap: React.Dispatch<React.SetStateAction<WarehouseMap | undefined>>;
  warehousesMap?: WarehouseMap[];
  setWarehousesMap: React.Dispatch<React.SetStateAction<WarehouseMap[] | undefined>>;
}

const WarehouseContext = createContext<WarehouseContextProps>({} as WarehouseContextProps);

interface IWarehouseProps {
  children: React.ReactChild;
}

const WarehouseProvider = ({ children }: IWarehouseProps) => {
  const [warehousesMap, setWarehousesMap] = useState<WarehouseMap[]>();
  const [selectedMap, setSelectedMap] = useState<WarehouseMap>();
  const [warehouse, setWarehouse] = useState<Warehouse>();
  const [sectionId, setSectionId] = useState<number>();
  const [searches, setSearches] = useState<Searches>();
  const [sorting, setSorting] = useState<Sorts>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [filters, setFilters] = useState<Filters>({ isActive: ["1"] });
  const { currentUser } = useAppState();

  const attributes = useMemo(
    () => ({
      section: ["id", "name"],
      warehouse: ["id", "name"],
      mapCells: [
        "id",
        "isActive",
        "columnName",
        "indexColumn",
        "indexLevel",
        "indexRow",
        "name",
        "rowName",
        "levelName",
      ],
      mapCellsItemsJobs: ["id", "description"],
      mapCellsStorageBoxes: ["id", "number"],
    }),
    []
  );

  const { refetch: getAllWarehouseMaps, isFetching: isFetchingWarehouseMaps } = useQuery(
    ["get.by.warehouse.id", warehouse, sectionId, searches, filters, sorting, page, pageSize],
    () =>
      WarehousesMapsService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy:
          sorting && sorting?.length > 0
            ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
            : JSON.stringify([["updatedAt", "desc"]]),
        include: JSON.stringify([
          { association: "configRow" },
          { association: "configLevel" },
          { association: "configColumn" },
          {
            association: "section",
            attributes: attributes.section,
            where: {
              id: sectionId,
            },
          },
          {
            association: "warehouse",
            attributes: attributes.warehouse,
            where: {
              id: warehouse?.id,
            },
          },
          {
            association: "mapCells",
            attributes: attributes.mapCells,
            include: [
              { association: "itemsJobs",
                attributes: attributes.mapCellsItemsJobs
              },
              { association: "storageBoxes",
                attributes: attributes.mapCellsStorageBoxes
              },
              { association: "status" },
            ],
          },
        ]),
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      onSuccess: (response) => {
        if (response && response.data) setWarehousesMap(response.data);
      },
      enabled: !!currentUser?.companyId && !!warehouse,
      refetchOnWindowFocus: false,
    }
  );

  return (
    <WarehouseContext.Provider
      value={{
        warehouse,
        setWarehouse,
        sectionId,
        setSectionId,
        searches,
        setSearches,
        sorting,
        setSorting,
        page,
        setPage,
        pageSize,
        setPageSize,
        filters,
        setFilters,
        getAllWarehouseMaps,
        isFetchingWarehouseMaps,
        selectedMap,
        setSelectedMap,
        warehousesMap,
        setWarehousesMap,
      }}
    >
      {children}
    </WarehouseContext.Provider>
  );
};

export { WarehouseContext, WarehouseProvider };
